import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import type { PlayingEntryWithGame } from '@/types'
import { useUIStore } from '@/store/ui'
import { useAuthStore } from '@/store/auth'
import { useRatingNudgeStore } from '@/store/ratingNudge'
import {
  fetchPlaying,
  promoteToPlaying,
  updatePlayingEntry,
  promoteToPlayed,
  rateGame,
} from '@/api/wishlist'
import { fetchGroupSummary } from '@/api/groups'
import { toFriend } from '@/lib/steam'
import { useDemoteToWishlist } from '@/hooks/useDemoteToWishlist'
import { useGroupPermissions } from '@/hooks/useGroupPermissions'
import { GroupHeader } from '@/components/groups/GroupHeader'
import { PlayingCard } from '@/components/playing/PlayingCard'
import { RatePromptModal } from '@/components/playing/RatePromptModal'
import { AddGameModal } from '@/components/wishlist/AddGameModal'
import { Spinner } from '@/components/ui/Spinner'
import { Icon } from '@/components/ui/Icon'

export default function PlayingPage() {
  const { activeGroupId } = useUIStore()
  const { user } = useAuthStore()
  const { canEditContent } = useGroupPermissions()
  const dismissRatingNudge = useRatingNudgeStore((s) => s.dismiss)
  const queryClient = useQueryClient()
  const [showAddModal, setShowAddModal] = useState(false)
  const [ratePrompt, setRatePrompt] = useState<PlayingEntryWithGame | null>(null)

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['playing', activeGroupId],
    queryFn: () => fetchPlaying(activeGroupId!),
    enabled: !!activeGroupId,
  })

  const { data: summary } = useQuery({
    queryKey: ['group-summary', activeGroupId],
    queryFn: () => fetchGroupSummary(activeGroupId!),
    enabled: !!activeGroupId,
  })
  const groupMembers = (summary?.members ?? [])
    .map(toFriend)
    .sort((a, b) => (a.nickname || a.name).localeCompare(b.nickname || b.name))

  const updateMutation = useMutation({
    mutationFn: ({ gameId, note }: { gameId: string; note: string }) =>
      updatePlayingEntry(activeGroupId!, gameId, note),
    onMutate: async ({ gameId, note }) => {
      await queryClient.cancelQueries({ queryKey: ['playing', activeGroupId] })
      const previous = queryClient.getQueryData<PlayingEntryWithGame[]>(['playing', activeGroupId])
      queryClient.setQueryData<PlayingEntryWithGame[]>(['playing', activeGroupId], (old = []) =>
        old.map((e) => e.gameId === gameId ? { ...e, note } : e)
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(['playing', activeGroupId], context.previous)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['playing', activeGroupId] }),
  })

  const promoteToPlayedMutation = useMutation({
    mutationFn: (gameId: string) => promoteToPlayed(activeGroupId!, gameId),
    onMutate: async (gameId) => {
      await queryClient.cancelQueries({ queryKey: ['playing', activeGroupId] })
      const previous = queryClient.getQueryData<PlayingEntryWithGame[]>(['playing', activeGroupId])
      queryClient.setQueryData<PlayingEntryWithGame[]>(['playing', activeGroupId], (old = []) =>
        old.filter((e) => e.gameId !== gameId)
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(['playing', activeGroupId], context.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['playing', activeGroupId] })
      queryClient.invalidateQueries({ queryKey: ['played', activeGroupId] })
    },
  })

  const rateMutation = useMutation({
    mutationFn: ({ gameId, rating }: { gameId: string; rating: number }) =>
      rateGame(activeGroupId!, gameId, rating),
    onMutate: async ({ gameId, rating }) => {
      await queryClient.cancelQueries({ queryKey: ['playing', activeGroupId] })
      const previous = queryClient.getQueryData<PlayingEntryWithGame[]>(['playing', activeGroupId])
      queryClient.setQueryData<PlayingEntryWithGame[]>(['playing', activeGroupId], (old = []) =>
        old.map((entry) => {
          if (entry.gameId !== gameId) return entry
          const ratings = { ...entry.ratings }
          if (rating === 0) delete ratings[user!.steamId]
          else ratings[user!.steamId] = rating
          return { ...entry, ratings }
        })
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(['playing', activeGroupId], context.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['playing', activeGroupId] })
      queryClient.invalidateQueries({ queryKey: ['played', activeGroupId] })
    },
  })

  const demoteToWishlistMutation = useDemoteToWishlist(activeGroupId!)

  const addToPlayingMutation = useMutation({
    mutationFn: (gameId: string) => promoteToPlaying(activeGroupId!, gameId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['playing', activeGroupId] })
      queryClient.invalidateQueries({ queryKey: ['wishlist', activeGroupId] })
      queryClient.invalidateQueries({ queryKey: ['played', activeGroupId] })
      setShowAddModal(false)
    },
  })

  if (!activeGroupId) {
    return (
      <div className="cg-screen">
        <div className="cg-empty">Select or create a group to see active sessions.</div>
      </div>
    )
  }

  const existingGameIds = new Set(entries.map((e) => e.gameId))

  return (
    <div className="cg-screen">
      <GroupHeader
        sublabel={entries.length > 0 ? `${entries.length} active session${entries.length !== 1 ? 's' : ''}` : undefined}
      />

      {canEditContent && (
        <div className="cg-toolbar">
          <div className="cg-toolbar-spacer" />
          <button className="cg-btn cg-btn-primary" onClick={() => setShowAddModal(true)}>
            <Icon name="plus" size={12} /> Add game
          </button>
        </div>
      )}

      {isLoading && <Spinner />}

      {!isLoading && entries.length === 0 && (
        <div className="cg-empty">
          {canEditContent ? (
            <>
              No active sessions —{' '}
              <button
                style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 'inherit' }}
                onClick={() => setShowAddModal(true)}
              >
                start one!
              </button>
            </>
          ) : (
            'No active sessions.'
          )}
        </div>
      )}

      {!isLoading && entries.length > 0 && (
        <div className="cg-playing-grid">
          {entries.map((entry) => (
            <PlayingCard
              key={entry.gameId}
              entry={entry}
              groupMembers={groupMembers}
              onUpdate={(note) => updateMutation.mutate({ gameId: entry.gameId, note })}
              onPromoteToPlayed={() => {
                promoteToPlayedMutation.mutate(entry.gameId)
                const myRating = (user && entry.ratings[user.steamId]) || 0
                if (myRating === 0) setRatePrompt(entry)
              }}
              onDemoteToWishlist={() => demoteToWishlistMutation.mutate(entry.gameId)}
              onRate={(rating) => rateMutation.mutate({ gameId: entry.gameId, rating })}
              canEdit={canEditContent}
            />
          ))}
        </div>
      )}

      {showAddModal && activeGroupId && (
        <AddGameModal
          groupId={activeGroupId}
          existingGameIds={existingGameIds}
          existingLabel="Already playing"
          onClose={() => setShowAddModal(false)}
          onAdd={(gameId) => addToPlayingMutation.mutate(gameId)}
          isAdding={addToPlayingMutation.isPending}
          title="Add to playing"
        />
      )}

      {ratePrompt && (
        <RatePromptModal
          game={ratePrompt.game}
          onSkip={() => {
            // Snooze the global nudge so it doesn't immediately re-prompt the
            // game the user just chose to skip (it was finished ~now).
            if (activeGroupId) dismissRatingNudge(activeGroupId, Date.now())
            setRatePrompt(null)
          }}
          onSubmit={(rating) => {
            rateMutation.mutate({ gameId: ratePrompt.gameId, rating })
            setRatePrompt(null)
          }}
        />
      )}
    </div>
  )
}
